<script setup lang="ts">
import { computed, nextTick, shallowRef, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import ProgressSpinner from 'primevue/progressspinner'
import BrowseLayout from '@/browse/BrowseLayout.vue'
import ActiveFilters from '@/browse/ActiveFilters.vue'
import BrowseGoalsFilters from '@/browse/goals/BrowseGoalsFilters.vue'
import BrowseGoalsResults from '@/browse/goals/BrowseGoalsResults.vue'
import { useBrowseSearch } from '@/browse/useBrowseSearch'
import { focusFilterControlWhenNoChipsRemain } from '@/browse/only_chip_focus'
import type { ChipRow } from '@/browse/browse_state'
import { GRADES } from '@/browse/grades'
import { goalBrowseConfig, goalChipRemoveButtonName } from '@/browse/goals/goal_browse'
import { characteristicLabelForUuid, getGoalCharacteristics } from '@/browse/goals/goal_characteristics'
import type { GoalCharacteristic } from '@/browse/goals/goal_characteristics'
import { uniqueStandardRefsForSetFromHits } from '@/browse/goals/goal_standards'
import { getBrowseGoalsClient } from '@/search/algolia_search'
import { getStandardSummaries } from '@/standards/api'
import type { StandardSummary } from '@/standards/api'
import type { GoalHit } from '@/goals/goal_types'
import { useUserStore } from '@/authentication/user.store'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import type { ContentProfileSubject } from '@/contentprofile/premium_locks'
import { useUserFeedbackContext } from '@/components/useUserFeedbackContext'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { EVENT_NAMES } from '@/tracking/analytics/event_names'
import { betaOptIn } from '@/util/beta_opt_in'
import { announce } from '@/util/live_announcer'
import { gradeLabel } from '@/util/gradesort'
import { setTitle } from '@/util/title'

const MAX_EVENT_KEYWORD_LENGTH = 120
const EMPTY_STATE_TIPS = [
  'Select a subject and/or a characteristic',
  'Filter by grade levels',
  'Choose a category for the subject selected above',
  'Filter by a particular standard',
  'Search using keywords'
]
const NO_GOALS_FOUND_MESSAGE = 'Sorry, we currently don\'t have learning goals matching your query; try broadening your search by de-selecting some criteria above.'

const route = useRoute()
const router = useRouter()
const { fetching: fetchingUser, isAuthenticated, isTrial } = storeToRefs(useUserStore())
const contentProfile = useContentProfileStore()
const { publishedSubjects: profilePublishedSubjects } = storeToRefs(contentProfile)
const publishedSubjects = computed(() => profilePublishedSubjects.value as ContentProfileSubject[])
const { updateContext: updateFeedbackContext } = useUserFeedbackContext()

const characteristics = shallowRef<GoalCharacteristic[] | null>(null)
getGoalCharacteristics()
  .then((loaded) => { characteristics.value = loaded })
  .catch((e) => {
    console.error('Could not load goal characteristics', e)
    characteristics.value = []
  })

function standardSetsForSubject(subject?: string): string[] {
  if (!subject) {
    return contentProfile.visibleStandardSets
  }
  return contentProfile.visibleStandardSetsForSubject(subject, { onlyPublished: true })
}

const config = goalBrowseConfig({
  standardSetsFor: standardSetsForSubject,
  publishedSubjects: () => (fetchingUser.value ? null : publishedSubjects.value),
  characteristics: () => characteristics.value,
  isReadyForSearch: () => !fetchingUser.value && characteristics.value !== null
})

const { state, hits, totalHits, queryID, facetCounts, isLoading, hasFailed, chipRows, chipRowsAfterRemoving, refine, toggle, removeChip, setKeyword } =
  useBrowseSearch<GoalHit>(config, getBrowseGoalsClient)

const refinements = computed(() => state.value.refinements)
const selectedSubject = computed(() => refinements.value.subject[0] ?? '')
const selectedCategory = computed(() => refinements.value.subjectDomain[0])
const selectedCharacteristicUuid = computed(() => refinements.value.characteristics[0])
const characteristicLabel = computed(() => characteristicLabelForUuid(characteristics.value ?? [], selectedCharacteristicUuid.value) ?? '')

const isSelectedSubjectPublished = computed(() => publishedSubjects.value.some((publishedSubject) => publishedSubject.subject === selectedSubject.value))
const isKeywordEntered = computed(() => Boolean(state.value.keyword))
const isCharacteristicSelected = computed(() => Boolean(selectedCharacteristicUuid.value))
const hasCriteria = computed(() => isSelectedSubjectPublished.value || isKeywordEntered.value || isCharacteristicSelected.value)

const noGoalsFound = computed(() => hasCriteria.value && !hasFailed.value && !isLoading.value && !hits.value.length && !fetchingUser.value)
// Mirrors Vue 2 (#4528): full-access users see a visible "Found 0 results" line, so it rides in
// this one announcement (live_announcer is last-writer-wins within a tick). BrowseGoalsResults'
// always-mounted status region separately announces "Found 0 results." to everyone.
const noGoalsFoundAnnouncement = computed(() =>
  isAuthenticated.value && !isTrial.value ? `${NO_GOALS_FOUND_MESSAGE} Found 0 results` : NO_GOALS_FOUND_MESSAGE
)

async function announceNoGoalsFound(found: boolean) {
  if (!found) {
    return
  }
  await nextTick()
  if (noGoalsFound.value) {
    announce(noGoalsFoundAnnouncement.value)
  }
}

const browseFilters = computed(() => ({ subject: selectedSubject.value, subjectDomain: selectedCategory.value }))

type ActiveFilterChip = ChipRow['chips'][number]

function gradeOrder(gradeChip: ActiveFilterChip): number {
  return GRADES.indexOf(gradeChip.value)
}

function withGradeChipsInGradeOrder(row: ChipRow): ChipRow {
  const isGradesRow = row.chips.every((chip) => chip.key === 'grades')
  if (!isGradesRow) {
    return row
  }
  const gradeChipsInGradeOrder = [...row.chips].sort((gradeChipA, gradeChipB) => gradeOrder(gradeChipA) - gradeOrder(gradeChipB))
  return { ...row, chips: gradeChipsInGradeOrder }
}

const sortedChipRows = computed(() => chipRows.value.map(withGradeChipsInGradeOrder))

const standards = shallowRef<StandardSummary[]>([])
const isLoadingStandards = shallowRef(false)
const standardsByLongRef = computed(() => Object.fromEntries(standards.value.map((standard) => [standard.standard_long_ref, standard])))
let latestStandardsRequest = 0

async function loadStandards() {
  const request = ++latestStandardsRequest
  const setCode = contentProfile.dropdownStandardSetForSubject(selectedSubject.value)
  const longRefs = selectedSubject.value ? uniqueStandardRefsForSetFromHits(hits.value, setCode) : []
  if (!longRefs.length) {
    standards.value = []
    isLoadingStandards.value = false
    return
  }
  isLoadingStandards.value = true
  try {
    const loaded = await getStandardSummaries(setCode, longRefs)
    if (request === latestStandardsRequest) {
      standards.value = loaded
    }
  } catch (e) {
    console.error('Could not load standards', e)
    if (request === latestStandardsRequest) {
      standards.value = []
    }
  } finally {
    if (request === latestStandardsRequest) {
      isLoadingStandards.value = false
    }
  }
}

const analytics = getAnalyticsTracker().getChildContext({
  browse_goals_filter_subject: selectedSubject,
  browse_goals_filter_grades: computed(() => refinements.value.grades),
  browse_goals_filter_category: computed(() => selectedCategory.value ?? ''),
  browse_goals_filter_standard: computed(() => refinements.value.standard[0] ?? ''),
  browse_goals_filter_level_of_support: computed(() => Number(refinements.value.levelOfSupport[0])),
  browse_goals_filter_keyword: computed(() => state.value.keyword.slice(0, MAX_EVENT_KEYWORD_LENGTH)),
  browse_goals_filter_characteristic: computed(() => characteristicLabel.value || null)
})

type AnalyticsEvent = [name: string, data: Record<string, unknown>]

function removedFilterEvent(key: string, value: string): AnalyticsEvent | undefined {
  switch (key) {
    case 'subject':
      return [EVENT_NAMES.BROWSE_GOALS.REMOVED_SUBJECT, {
        browse_goals_removed_subject: value,
        browse_goals_removed_category: selectedCategory.value,
        browse_goals_removed_standard: refinements.value.standard[0]
      }]
    case 'subjectDomain':
      return [EVENT_NAMES.BROWSE_GOALS.REMOVED_CATEGORY, { browse_goals_removed_category: value }]
    case 'grades':
      return [EVENT_NAMES.BROWSE_GOALS.REMOVED_GRADE, { browse_goals_removed_grade: value }]
    case 'keyword':
      return [EVENT_NAMES.BROWSE_GOALS.REMOVED_KEYWORD, { browse_goals_removed_keyword: value.slice(0, MAX_EVENT_KEYWORD_LENGTH) }]
    case 'standard':
      return [EVENT_NAMES.BROWSE_GOALS.REMOVED_STANDARD, { browse_goals_removed_standard: value }]
    case 'characteristics':
      return [EVENT_NAMES.BROWSE_GOALS.REMOVED_CHARACTERISTIC, { browse_goals_removed_characteristic: [characteristicLabel.value] }]
  }
  return undefined
}

// The event names the filter as it was, but its filter context must describe the filters left after removing it.
async function removeFilter(key: string, value: string) {
  const event = removedFilterEvent(key, value)
  focusFilterControlWhenNoChipsRemain(chipRowsAfterRemoving(key, value))
  await removeChip(key, value)
  if (event) {
    analytics.sendEvent(...event)
  }
}

function chipLabel(key: string, value: string): string {
  if (key === 'grades') {
    return gradeLabel(value)
  }
  if (key === 'characteristics') {
    return characteristicLabel.value || value
  }
  return value
}

function showRelatedSubject(relatedSubject: string) {
  return router.replace({ query: { subject: relatedSubject, grades: refinements.value.grades, ...betaOptIn(route.query) } })
}

const pageTitle = computed(() => {
  const grades = refinements.value.grades
  const gradesPhrase = grades.length ? `for grades ${grades.join(', ')}` : ''
  const filtersPhrase = [selectedSubject.value, selectedCategory.value, gradesPhrase].filter(Boolean).join(' ')
  if (!filtersPhrase) {
    return 'Browse Goals'
  }
  return `${filtersPhrase} Goals`
})

watch([selectedSubject, hits], loadStandards)
watch(pageTitle, setTitle, { immediate: true })
watch(noGoalsFound, announceNoGoalsFound, { immediate: true })
watch(selectedSubject, (current) => updateFeedbackContext({ subject: current }), { immediate: true })

analytics.sendEvent(EVENT_NAMES.BROWSE_GOALS.VIEWED_PAGE, { ref: route.query.ref })
</script>

<template>
  <BrowseLayout
    sticky-filters
    title="Browse Learning Goals"
    description="Search through our entire collection of learning goals, objectives, and progress monitoring resources."
  >
    <template v-if="!fetchingUser" #filters>
      <BrowseGoalsFilters
        :state="state"
        :facet-counts="facetCounts"
        :characteristics="characteristics"
        :standards="standards"
        :is-loading-standards="isLoadingStandards"
        :is-searching="isLoading"
        @refine="refine"
        @toggle="toggle"
        @keyword="setKeyword"
      />
    </template>

    <div v-if="fetchingUser" class="flex items-center justify-center text-text-md text-gray-700">
      <ProgressSpinner stroke-width="8" class="!ml-0 !mr-2 h-4 w-4 stroke-gray-700" aria-hidden="true" />
      Loading...
    </div>
    <ul v-else-if="!hasCriteria" class="list-none">
      <li v-for="tip in EMPTY_STATE_TIPS" :key="tip" class="mb-14 mt-6 text-[25px] font-[300] leading-10 text-gray-800">{{ tip }}</li>
    </ul>
    <template v-else>
      <ActiveFilters :rows="sortedChipRows" :chip-label="chipLabel" :remove-button-name="goalChipRemoveButtonName" @remove="removeFilter" />
      <div v-if="hasFailed" role="alert" class="mb-4 rounded-xs bg-red-100 p-3 text-text-md text-red-400">
        Something went wrong while searching. Please reload the page to try again.
      </div>
      <div
        v-else-if="noGoalsFound"
        aria-live="polite"
        class="mb-4 border border-blue-300 bg-blue-200 py-2 pl-3.5 pr-9 text-text-md text-blue-500"
      >
        {{ NO_GOALS_FOUND_MESSAGE }}
      </div>
      <BrowseGoalsResults
        v-if="!hasFailed"
        :goals="hits"
        :total-results="totalHits"
        :is-loading="isLoading"
        :selected-subject="selectedSubject"
        :is-keyword-active="isKeywordEntered"
        :algolia-query-id="queryID"
        :standards-by-long-ref="standardsByLongRef"
        :browse-filters="browseFilters"
        @related-subject="showRelatedSubject"
      />
    </template>
  </BrowseLayout>
</template>
